import axios from 'axios';
import { getConfigValue } from 'utils/site';

// Configuración base del servicio (usa localStorage directo para no redirigir en páginas públicas)
// Para visitantes públicos sin sesión, usa la variable de entorno REACT_APP_WP_API_URL como fallback
const getAPIConfig = () => {
  let wpApiUrl = null;

  // Intentar leer WP_API_URL desde site_config en localStorage (usuarios con sesión)
  try {
    const raw = localStorage.getItem('site_config');
    if (raw) {
      const siteConfig = JSON.parse(raw);
      wpApiUrl = getConfigValue(siteConfig, 'WP_API_URL');
    }
  } catch (_) {}

  // Fallback a variable de entorno (visitantes públicos sin site_config)
  if (!wpApiUrl && process.env.REACT_APP_WP_API_URL) {
    wpApiUrl = `${process.env.REACT_APP_WP_API_URL}/wp-json`;
  }

  if (!wpApiUrl) {
    throw new Error('WP_API_URL no está configurado');
  }

  return {
    baseURL: `${wpApiUrl}/datofy/v1`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  };
};

// Cliente API para appointment booking
const createAppointmentAPI = () => {
  const config = getAPIConfig();
  const api = axios.create(config);

  // Interceptor para manejo de errores
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('Error en API de Appointment Booking:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Error en el servidor';
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('Error de conexión. Verifica tu conexión a internet.');
      } else {
        throw new Error('Error en la configuración de la solicitud.');
      }
    }
  );

  return api;
};

// ===== PERFIL PÚBLICO DEL PROFESOR =====

/**
 * Obtener perfil público de un profesor desde datofy-api (Laravel).
 * Endpoint público: GET /api/public/teacher/{username}
 */
export const getTeacherProfile = async (teacherId) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SURVEY_API_URL}/api/public/teacher/${teacherId}`
    );
    return response.data;
  } catch (error) {
    const serverMessage = error.response?.data?.message;
    if (serverMessage) return { success: false, message: serverMessage };
    throw error;
  }
};

// ===== FUNCIONES DE DISPONIBILIDAD (PROFESORES) =====

/**
 * Obtener disponibilidad de un profesor
 * @param {number|string} teacherId - ID del profesor
 * @param {Object} options - Opciones de consulta
 * @param {string} options.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} options.dateTo - Fecha fin (YYYY-MM-DD)
 * @param {string} options.status - Estado (available, booked, blocked)
 * @returns {Promise<Object>} Bloques de disponibilidad
 */
export const getTeacherAvailability = async (teacherId, options = {}) => {
  try {
    const api = createAppointmentAPI();
    const params = new URLSearchParams();

    if (options.dateFrom) params.append('date_from', options.dateFrom);
    if (options.dateTo) params.append('date_to', options.dateTo);
    if (options.status) params.append('status', options.status);

    const response = await api.get(`/agenda/availability/${teacherId}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener disponibilidad del profesor ${teacherId}:`, error);
    throw error;
  }
};

/**
 * Crear bloque de disponibilidad
 * @param {Object} availabilityData - Datos del bloque de disponibilidad
 * @param {number} availabilityData.teacher_id - ID del profesor
 * @param {string} availabilityData.start_time - Hora inicio (HH:mm)
 * @param {string} availabilityData.end_time - Hora fin (HH:mm)
 * @param {string} availabilityData.date - Fecha específica (YYYY-MM-DD) o null para recurrente
 * @param {string} availabilityData.day_of_week - Día de la semana (0-6) para recurrente
 * @param {number} availabilityData.duration_minutes - Duración de cada slot en minutos
 * @param {string} availabilityData.repeat_pattern - Patrón repetición (once, weekly, monthly)
 * @param {string} availabilityData.repeat_until - Fecha límite repetición (YYYY-MM-DD)
 * @returns {Promise<Object>} Bloque creado
 */
export const createAvailability = async (availabilityData) => {
  try {
    const api = createAppointmentAPI();

    // Validaciones básicas
    if (!availabilityData.teacher_id || !availabilityData.start_time || !availabilityData.end_time) {
      throw new Error('ID del profesor, hora inicio y hora fin son requeridos');
    }

    const response = await api.post('/agenda/availability', availabilityData);
    return response.data;
  } catch (error) {
    const serverMessage = error.response?.data?.message;
    if (serverMessage) {
      return { success: false, message: serverMessage };
    }
    console.error('Error al crear disponibilidad:', error);
    throw error;
  }
};

/**
 * Actualizar bloque de disponibilidad
 * @param {number|string} availabilityId - ID del bloque de disponibilidad
 * @param {Object} updateData - Datos a actualizar
 * @returns {Promise<Object>} Bloque actualizado
 */
export const updateAvailability = async (availabilityId, updateData) => {
  try {
    const api = createAppointmentAPI();
    
    if (!availabilityId) {
      throw new Error('ID del bloque de disponibilidad es requerido');
    }

    const response = await api.put(`/agenda/availability/${availabilityId}`, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error al actualizar disponibilidad ${availabilityId}:`, error);
    throw error;
  }
};

/**
 * Eliminar bloque de disponibilidad
 * @param {number|string} availabilityId - ID del bloque de disponibilidad
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deleteAvailability = async (availabilityId) => {
  try {
    const api = createAppointmentAPI();
    
    if (!availabilityId) {
      throw new Error('ID del bloque de disponibilidad es requerido');
    }

    const response = await api.delete(`/agenda/availability/${availabilityId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al eliminar disponibilidad ${availabilityId}:`, error);
    throw error;
  }
};

// ===== BLOQUEOS PUNTUALES (EXCEPCIONES) SOBRE DISPONIBILIDAD =====

/**
 * Obtener bloqueos puntuales de un profesor
 * @param {number|string} teacherId - ID del profesor
 * @param {Object} options - Opciones de consulta
 * @param {string} options.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} options.dateTo - Fecha fin (YYYY-MM-DD)
 * @returns {Promise<Object>} Bloqueos puntuales
 */
export const getAvailabilityExceptions = async (teacherId, options = {}) => {
  try {
    const api = createAppointmentAPI();
    const params = new URLSearchParams();

    if (options.dateFrom) params.append('date_from', options.dateFrom);
    if (options.dateTo) params.append('date_to', options.dateTo);

    const response = await api.get(`/agenda/availability-exceptions/${teacherId}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener bloqueos del profesor ${teacherId}:`, error);
    throw error;
  }
};

/**
 * Crear un bloqueo puntual sobre una fecha, sin modificar las reglas recurrentes/específicas.
 * @param {Object} exceptionData - Datos del bloqueo
 * @param {number} exceptionData.teacher_id - ID del profesor
 * @param {string} exceptionData.date - Fecha a bloquear (YYYY-MM-DD)
 * @param {number} [exceptionData.availability_id] - Si se indica, bloquea solo ese bloque; si no, bloquea todos los bloques ese día
 * @param {string} [exceptionData.start_time] - Si se indica junto a end_time, bloquea solo ese rango horario; si no, bloquea el día completo
 * @param {string} [exceptionData.end_time]
 * @param {string} [exceptionData.reason] - Motivo del bloqueo (opcional)
 * @returns {Promise<Object>} Bloqueo creado
 */
export const createAvailabilityException = async (exceptionData) => {
  try {
    const api = createAppointmentAPI();

    if (!exceptionData.teacher_id || !exceptionData.date) {
      throw new Error('ID del profesor y fecha son requeridos');
    }

    const response = await api.post('/agenda/availability-exceptions', exceptionData);
    return response.data;
  } catch (error) {
    const serverMessage = error.response?.data?.message;
    if (serverMessage) {
      return { success: false, message: serverMessage };
    }
    console.error('Error al crear bloqueo puntual:', error);
    throw error;
  }
};

/**
 * Eliminar un bloqueo puntual (no afecta la regla de disponibilidad recurrente/específica)
 * @param {number|string} exceptionId - ID del bloqueo
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deleteAvailabilityException = async (exceptionId) => {
  try {
    const api = createAppointmentAPI();

    if (!exceptionId) {
      throw new Error('ID del bloqueo es requerido');
    }

    const response = await api.delete(`/agenda/availability-exceptions/${exceptionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al eliminar bloqueo ${exceptionId}:`, error);
    throw error;
  }
};

// ===== FUNCIONES DE CITAS (APODERADOS/PÚBLICO) =====

/**
 * Validar RUT de estudiante y obtener datos
 * @param {string} rut - RUT del estudiante (con o sin puntos/guión)
 * @returns {Promise<Object>} Datos del estudiante validado
 */
export const validateStudentRUT = async (rut) => {
  try {
    const api = createAppointmentAPI();
    
    if (!rut || rut.trim().length === 0) {
      throw new Error('RUT es requerido');
    }

    // Limpiar RUT: solo quitar puntos, conservar el guión (username WP = "23799671-2")
    const cleanRUT = rut.replace(/\./g, '').trim();

    const response = await api.post('/agenda/validate-student', { rut: cleanRUT });
    return response.data;
  } catch (error) {
    console.error('Error al validar RUT del estudiante:', error);
    throw error;
  }
};

/**
 * Obtener horarios disponibles para reservar
 * @param {Object} filters - Filtros de búsqueda
 * @param {string} filters.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} filters.dateTo - Fecha fin (YYYY-MM-DD)
 * @param {number} filters.teacherId - ID del profesor específico (opcional)
 * @param {number} filters.duration - Duración mínima en minutos (opcional)
 * @returns {Promise<Object>} Horarios disponibles para reservar
 */
export const getAvailableSlots = async (filters = {}) => {
  try {
    const api = createAppointmentAPI();
    const params = new URLSearchParams();

    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    if (filters.teacherId) params.append('teacher_id', filters.teacherId);
    if (filters.duration) params.append('duration', filters.duration);

    const response = await api.get(`/agenda/available-slots?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener horarios disponibles:', error);
    throw error;
  }
};

/**
 * Reservar una cita (endpoint público)
 * @param {Object} appointmentData - Datos de la cita
 * @param {string} appointmentData.student_rut - RUT del estudiante
 * @param {number} appointmentData.teacher_id - ID del profesor
 * @param {string} appointmentData.appointment_date - Fecha de la cita (YYYY-MM-DD)
 * @param {string} appointmentData.start_time - Hora inicio (HH:mm)
 * @param {string} appointmentData.end_time - Hora fin (HH:mm)
 * @param {string} appointmentData.guardian_name - Nombre del apoderado
 * @param {string} appointmentData.guardian_email - Email del apoderado
 * @param {string} appointmentData.guardian_phone - Teléfono del apoderado (opcional)
 * @param {string} appointmentData.notes - Notas adicionales (opcional)
 * @returns {Promise<Object>} Cita creada
 */
export const bookAppointment = async (appointmentData) => {
  try {
    const api = createAppointmentAPI();
    
    // Validaciones básicas
    const required = ['student_rut', 'teacher_id', 'appointment_date', 'start_time', 'end_time', 'guardian_name', 'guardian_email'];
    for (const field of required) {
      if (!appointmentData[field]) {
        throw new Error(`El campo ${field} es requerido`);
      }
    }

    const response = await api.post('/agenda/book-appointment', appointmentData);
    return response.data;
  } catch (error) {
    console.error('Error al reservar cita:', error);
    throw error;
  }
};

/**
 * Obtener citas de un estudiante por RUT
 * @param {string} studentRut - RUT del estudiante
 * @param {Object} options - Opciones de consulta
 * @param {string} options.status - Estado de citas (scheduled, completed, cancelled)
 * @param {string} options.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} options.dateTo - Fecha fin (YYYY-MM-DD)
 * @returns {Promise<Object>} Citas del estudiante
 */
export const getStudentAppointments = async (studentRut, options = {}) => {
  try {
    const api = createAppointmentAPI();
    
    if (!studentRut || studentRut.trim().length === 0) {
      throw new Error('RUT del estudiante es requerido');
    }

    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.dateFrom) params.append('date_from', options.dateFrom);
    if (options.dateTo) params.append('date_to', options.dateTo);

    // Limpiar RUT: solo puntos, el guion antes del dígito verificador se mantiene
    const cleanRUT = studentRut.replace(/\./g, '').trim();

    const response = await api.get(`/agenda/appointments/student/${cleanRUT}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener citas del estudiante ${studentRut}:`, error);
    throw error;
  }
};

/**
 * Cancelar una cita
 * @param {number|string} appointmentId - ID de la cita
 * @param {string} reason - Razón de cancelación (opcional)
 * @returns {Promise<Object>} Resultado de la cancelación
 */
export const cancelAppointment = async (appointmentId, reason = '') => {
  try {
    const api = createAppointmentAPI();
    
    if (!appointmentId) {
      throw new Error('ID de la cita es requerido');
    }

    const response = await api.put(`/agenda/appointment/${appointmentId}/cancel`, { reason });
    return response.data;
  } catch (error) {
    console.error(`Error al cancelar cita ${appointmentId}:`, error);
    throw error;
  }
};

// ===== FUNCIONES DE GESTIÓN PARA PROFESORES =====

/**
 * Obtener todas las citas de un profesor
 * @param {number|string} teacherId - ID del profesor
 * @param {Object} options - Opciones de consulta
 * @param {string} options.status - Estado de citas
 * @param {string} options.dateFrom - Fecha inicio
 * @param {string} options.dateTo - Fecha fin
 * @returns {Promise<Object>} Citas del profesor
 */
export const getTeacherAppointments = async (teacherId, options = {}) => {
  try {
    const api = createAppointmentAPI();
    const params = new URLSearchParams();

    if (options.status) params.append('status', options.status);
    if (options.dateFrom) params.append('date_from', options.dateFrom);
    if (options.dateTo) params.append('date_to', options.dateTo);

    const response = await api.get(`/agenda/appointments/teacher/${teacherId}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener citas del profesor ${teacherId}:`, error);
    throw error;
  }
};

/**
 * Confirmar o completar una cita
 * @param {number|string} appointmentId - ID de la cita
 * @param {string} status - Nuevo estado (confirmed, completed)
 * @param {string} notes - Notas del profesor (opcional)
 * @returns {Promise<Object>} Cita actualizada
 */
export const updateAppointmentStatus = async (appointmentId, status, notes = '') => {
  try {
    const api = createAppointmentAPI();
    
    if (!appointmentId || !status) {
      throw new Error('ID de la cita y estado son requeridos');
    }

    const response = await api.put(`/agenda/appointment/${appointmentId}/status`, { status, notes });
    return response.data;
  } catch (error) {
    console.error(`Error al actualizar estado de cita ${appointmentId}:`, error);
    throw error;
  }
};

// ===== VISTA ADMIN: TODAS LAS CITAS =====

/**
 * Obtener todas las citas de todos los profesores (vista admin)
 * @param {Object} options
 * @param {number} [options.teacherId] - Filtrar por profesor
 * @param {string} [options.status] - Filtrar por estado
 * @param {string} [options.dateFrom] - Fecha inicio (YYYY-MM-DD)
 * @param {string} [options.dateTo] - Fecha fin (YYYY-MM-DD)
 * @param {string} [options.search] - Búsqueda libre (nombre, email, código)
 * @param {string} [options.confirmationCode] - Código exacto
 * @param {number} [options.page]
 * @param {number} [options.perPage]
 */
export const getAllAppointments = async (options = {}) => {
  try {
    const api = createAppointmentAPI();
    const params = new URLSearchParams();

    if (options.teacherId)        params.append('teacher_id',        options.teacherId);
    if (options.status)           params.append('status',            options.status);
    if (options.dateFrom)         params.append('date_from',         options.dateFrom);
    if (options.dateTo)           params.append('date_to',           options.dateTo);
    if (options.search)           params.append('search',            options.search);
    if (options.confirmationCode) params.append('confirmation_code', options.confirmationCode);
    if (options.page)             params.append('page',              options.page);
    if (options.perPage)          params.append('per_page',          options.perPage);

    const response = await api.get(`/agenda/appointments?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener todas las citas:', error);
    throw error;
  }
};

// ===== UTILIDADES =====

/**
 * Generar slots de tiempo disponibles dentro de un bloque
 * @param {string} startTime - Hora inicio (HH:mm)
 * @param {string} endTime - Hora fin (HH:mm)
 * @param {number} slotDuration - Duración de cada slot en minutos
 * @returns {Array<Object>} Lista de slots de tiempo
 */
export const generateTimeSlots = (startTime, endTime, slotDuration = 30) => {
  const slots = [];
  const start = new Date(`2000-01-01T${startTime}:00`);
  const end = new Date(`2000-01-01T${endTime}:00`);
  
  let current = new Date(start);
  
  while (current < end) {
    const slotEnd = new Date(current.getTime() + (slotDuration * 60000));
    
    if (slotEnd <= end) {
      slots.push({
        start_time: current.toTimeString().slice(0, 5),
        end_time: slotEnd.toTimeString().slice(0, 5),
        duration: slotDuration
      });
    }
    
    current = slotEnd;
  }
  
  return slots;
};

/**
 * Formatear RUT chileno
 * @param {string} rut - RUT sin formato
 * @returns {string} RUT formateado (XX.XXX.XXX-X)
 */
export const formatRUT = (rut) => {
  if (!rut) return '';
  
  // Limpiar RUT
  const cleanRUT = rut.replace(/[^0-9kK]/g, '');
  
  if (cleanRUT.length < 2) return cleanRUT;
  
  // Separar número y dígito verificador
  const number = cleanRUT.slice(0, -1);
  const verifier = cleanRUT.slice(-1).toLowerCase();
  
  // Formatear número con puntos
  const formattedNumber = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${formattedNumber}-${verifier}`;
};

/**
 * Validar formato de RUT chileno
 * @param {string} rut - RUT a validar
 * @returns {boolean} true si el RUT es válido
 */
export const validateRUT = (rut) => {
  if (!rut) return false;
  
  // Limpiar RUT
  const cleanRUT = rut.replace(/[^0-9kK]/g, '');
  
  if (cleanRUT.length < 8 || cleanRUT.length > 9) return false;
  
  const number = cleanRUT.slice(0, -1);
  const verifier = cleanRUT.slice(-1).toLowerCase();
  
  // Calcular dígito verificador
  let sum = 0;
  let multiplier = 2;
  
  for (let i = number.length - 1; i >= 0; i--) {
    sum += parseInt(number[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const calculatedVerifier = remainder === 0 ? '0' : remainder === 1 ? 'k' : (11 - remainder).toString();
  
  return verifier === calculatedVerifier;
};

// Exportar todas las funciones por defecto
export default {
  // Disponibilidad
  getTeacherAvailability,
  createAvailability,
  updateAvailability,
  deleteAvailability,
  getAvailabilityExceptions,
  createAvailabilityException,
  deleteAvailabilityException,

  // Citas públicas
  validateStudentRUT,
  getAvailableSlots,
  bookAppointment,
  getStudentAppointments,
  cancelAppointment,
  
  // Gestión profesor
  getTeacherAppointments,
  updateAppointmentStatus,
  getAllAppointments,
  
  // Utilidades
  generateTimeSlots,
  formatRUT,
  validateRUT
};